import { Helmet } from "react-helmet";

import { Form, FormikProps, Formik } from "formik";
import Avatar from "@mui/material/Avatar";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Fab from "@mui/material/Fab";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { FormikTextField } from "../../components/Layout/FormikTextField";
import AuthSkeleton from "../auth/AuthSkeleton";

import { checkStatusCaptchaSchema } from "../auth/validation";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { selectAuth } from "../auth/authSlice";
import Toast from "../../components/Layout/Toast";
import { Container, Stack } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import { createRef, useMemo, useState } from "react";
import { RecaptchaCheckStatus } from "../auth/types/authType";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { checkStatusRecaptcha } from "./processSlices";
import { selectProcesses } from "./processSlices";
import { countries } from "../../components/dashboard/CountrySelect";

export const CheckStatus = () => {
  const { loading, error } = useAppSelector(selectAuth);
  const { selectedEmployee, loading: processLoading } =
    useAppSelector(selectProcesses);
  const { t: tt } = useTranslation();
  const { t: te } = useTranslation("enums");
  const [recaptchaCheckStatus, setRecaptchaCheckStatus] =
    useState<RecaptchaCheckStatus>({
      passportNumber: "",
      captcha: "",
    });
  const [hasSearched, setHasSearched] = useState(false);
  const dispatch = useAppDispatch();

  const recaptchaRef = createRef<ReCAPTCHA>();
  const appliedCountryKey =
    selectedEmployee?.additionalData?.appliedCountry ||
    (selectedEmployee as any)?.appliedCountry;
  const appliedCountryMeta = useMemo(() => {
    if (!appliedCountryKey) return null;
    const matchedCountry = countries.find(
      (country) => country.label === appliedCountryKey,
    );
    return {
      label: te(appliedCountryKey),
      flagCode: matchedCountry?.code?.toLowerCase(),
    };
  }, [appliedCountryKey, te]);
  const onReCAPTCHAChange = async (captchaCode: string | undefined | null) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!captchaCode) {
      return;
    }
    try {
      dispatch(
        checkStatusRecaptcha({ ...recaptchaCheckStatus, captcha: captchaCode }),
      );
    } catch (error) {
      //setError(error?.message || "Something went wrong");
    } finally {
      // Reset the reCAPTCHA when the request has failed or succeeded
      // so that it can be executed again if user submits another email.

      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };
  return (
    <>
      <Helmet>
        <title>Check Status | CvPlus</title>
      </Helmet>
      <Container
        maxWidth="md"
        sx={{ minHeight: "100vh", py: { xs: 2, md: 4 } }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            boxShadow: "0 14px 40px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 4 },
              py: { xs: 3, md: 4 },
              background:
                "linear-gradient(180deg, rgba(156,39,176,0.16) 0%, rgba(156,39,176,0.02) 100%)",
            }}
          >
            <Stack direction="row" justifyContent="flex-start" sx={{ mb: 1.5 }}>
              <Button
                component={RouterLink}
                to="/home"
                variant="outlined"
                size="small"
                startIcon={<ArrowBackRounded fontSize="small" />}
                sx={{ textTransform: "none", borderRadius: 999 }}
              >
                {tt("back")} {tt("home")}
              </Button>
            </Stack>
            <Typography
              variant="h4"
              fontWeight={800}
              color="secondary.main"
              textAlign={{ xs: "left", md: "center" }}
            >
              {tt("statusTracking")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 1 }}
              textAlign={{ xs: "left", md: "center" }}
            >
              {tt("search")} {tt("passportNumber")} / Visa Number / EmployerId
              {" - "}
              {tt("currentStatus")}
            </Typography>
          </Box>

          <Divider />

          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack spacing={2}>
              {loading === "pending" ? (
                <AuthSkeleton />
              ) : (
                <>
                  <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Formik
                        initialValues={recaptchaCheckStatus}
                        validationSchema={checkStatusCaptchaSchema}
                        onSubmit={(values, actions) => {
                          actions.setSubmitting(false);
                          setHasSearched(true);
                          recaptchaRef.current!.execute();
                          setRecaptchaCheckStatus({ ...values });
                        }}
                      >
                        {(props: FormikProps<RecaptchaCheckStatus>) => (
                          <Form>
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              size="invisible"
                              sitekey={
                                process.env
                                  .REACT_APP_RECAPTCHA_SITE_KEY as string
                              }
                              onChange={onReCAPTCHAChange}
                            />
                            <Grid container spacing={1.5}>
                              <Grid item xs={12}>
                                <FormikTextField
                                  formikKey="passportNumber"
                                  label="Passport Number / Visa Number / Visa Passport"
                                />
                              </Grid>
                            </Grid>

                            {error && (
                              <Box sx={{ mt: 1.5 }}>
                                <Toast severity="error">{error.message}</Toast>
                              </Box>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                              <Button
                                sx={{ width: "100%" }}
                                type="submit"
                                color="secondary"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={!props.isValid}
                              >
                                {tt("search")} {tt("employee")}
                              </Button>
                            </Box>
                          </Form>
                        )}
                      </Formik>
                    </CardContent>
                  </Card>

                  {processLoading === "pending" ? (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: "secondary.light",
                        background:
                          "linear-gradient(180deg, rgba(156,39,176,0.08) 0%, rgba(255,255,255,1) 65%)",
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={1.5}
                          >
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <Skeleton
                                variant="circular"
                                width={74}
                                height={74}
                              />
                              <Stack spacing={1}>
                                <Skeleton
                                  variant="text"
                                  width={160}
                                  height={32}
                                />
                                <Skeleton
                                  variant="text"
                                  width={190}
                                  height={24}
                                />
                              </Stack>
                            </Stack>
                            <Stack
                              direction="row"
                              spacing={1.2}
                              alignItems="center"
                            >
                              <Skeleton
                                variant="rounded"
                                width={90}
                                height={38}
                              />
                              <Skeleton
                                variant="rounded"
                                width={130}
                                height={34}
                              />
                            </Stack>
                          </Stack>
                          <Divider />
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Skeleton
                                variant="text"
                                width={110}
                                height={20}
                              />
                              <Skeleton
                                variant="text"
                                width={140}
                                height={28}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Skeleton variant="text" width={90} height={20} />
                              <Skeleton
                                variant="text"
                                width="100%"
                                height={28}
                              />
                            </Grid>
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : selectedEmployee?.passportNumber ? (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: "secondary.light",
                        background:
                          "linear-gradient(180deg, rgba(156,39,176,0.08) 0%, rgba(255,255,255,1) 65%)",
                      }}
                    >
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            spacing={1.5}
                          >
                            <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                            >
                              <Avatar
                                src={selectedEmployee.facePhoto || ""}
                                alt={
                                  selectedEmployee.givenName || "Employee face"
                                }
                                sx={{
                                  width: 74,
                                  height: 74,
                                  border: "2px solid",
                                  borderColor: "secondary.light",
                                }}
                              />
                              <Stack>
                                <Typography variant="h6" fontWeight={700}>
                                  {selectedEmployee.givenName}{" "}
                                  {selectedEmployee.surName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {tt("passportNo")}:{" "}
                                  {selectedEmployee.passportNumber}
                                </Typography>
                              </Stack>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.2}
                              alignItems="center"
                            >
                              {appliedCountryMeta && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                    color: "secondary.main",
                                    fontWeight: 700,
                                    pr: 0.5,
                                  }}
                                >
                                  {appliedCountryMeta.flagCode && (
                                    <Tooltip title={appliedCountryMeta.label}>
                                      <Fab
                                        component="span"
                                        color="secondary"
                                        size="small"
                                      >
                                        <Avatar
                                          alt={`${appliedCountryMeta.label} flag`}
                                          src={`https://flagcdn.com/w320/${appliedCountryMeta.flagCode}.png`}
                                          sx={{ height: 36, width: 36 }}
                                        />
                                      </Fab>
                                    </Tooltip>
                                  )}
                                  {appliedCountryMeta.label}
                                </Box>
                              )}
                              <Chip
                                color="secondary"
                                size="medium"
                                icon={<BadgeOutlined />}
                                label={
                                  selectedEmployee.status
                                    ? te(selectedEmployee.status)
                                    : "-"
                                }
                                sx={{ fontWeight: 700 }}
                              />
                            </Stack>
                          </Stack>

                          <Divider />

                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {tt("currentStatus")}
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {selectedEmployee.currentStatusDate
                                  ? new Date(
                                      selectedEmployee.currentStatusDate,
                                    ).toLocaleDateString()
                                  : "-"}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {tt("remarks")}
                              </Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {selectedEmployee.currentStatusRemark || "-"}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: hasSearched ? "warning.light" : "divider",
                        background: hasSearched
                          ? "linear-gradient(180deg, rgba(255,152,0,0.12) 0%, rgba(255,255,255,1) 75%)"
                          : undefined,
                      }}
                    >
                      <CardContent>
                        {hasSearched ? (
                          <Stack spacing={1.25} alignItems="center" textAlign="center">
                            <Avatar
                              sx={{
                                bgcolor: "warning.main",
                                color: "warning.contrastText",
                                width: 44,
                                height: 44,
                              }}
                            >
                              <ReportProblemOutlined />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight={700} color="warning.dark">
                              Search Unsuccessful
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {error?.message ||
                                "No status found for this passport, visa number, or employer ID."}
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                          >
                            {`${tt("search")} Passport Number / Visa Number / EmployerId ${tt(
                              "to",
                            )} ${tt("statusTracking").toLowerCase()}.`}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Paper>
      </Container>
    </>
  );
};
